/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { deviceIntegrationsRetrySchedule } from "../funcs/deviceIntegrationsRetrySchedule.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeviceIntegrationScheduleState } from "../models/components/deviceintegrationschedulestate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RetryDeviceIntegrationScheduleRequest,
  RetryDeviceIntegrationScheduleSecurity,
} from "../models/operations/retrydeviceintegrationschedule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RetryDeviceIntegrationScheduleMutationVariables = {
  request: RetryDeviceIntegrationScheduleRequest;
  security?: RetryDeviceIntegrationScheduleSecurity | undefined;
  options?: RequestOptions;
};

export type RetryDeviceIntegrationScheduleMutationData =
  DeviceIntegrationScheduleState;

export type RetryDeviceIntegrationScheduleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * retrySchedule deviceIntegrations
 *
 * @remarks
 * Make one sync schedule due immediately, lifting any automatic pause and resetting its failure streak.
 */
export function useRetryDeviceIntegrationScheduleMutation(
  options?: MutationHookOptions<
    RetryDeviceIntegrationScheduleMutationData,
    RetryDeviceIntegrationScheduleMutationError,
    RetryDeviceIntegrationScheduleMutationVariables
  >,
): UseMutationResult<
  RetryDeviceIntegrationScheduleMutationData,
  RetryDeviceIntegrationScheduleMutationError,
  RetryDeviceIntegrationScheduleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRetryDeviceIntegrationScheduleMutation(client, options),
    ...options,
  });
}

export function mutationKeyRetryDeviceIntegrationSchedule(): MutationKey {
  return ["@gram/client", "deviceIntegrations", "retrySchedule"];
}

export function buildRetryDeviceIntegrationScheduleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RetryDeviceIntegrationScheduleMutationVariables,
  ) => Promise<RetryDeviceIntegrationScheduleMutationData>;
} {
  return {
    mutationKey: mutationKeyRetryDeviceIntegrationSchedule(),
    mutationFn: function retryDeviceIntegrationScheduleMutationFn({
      request,
      security,
      options,
    }): Promise<RetryDeviceIntegrationScheduleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(deviceIntegrationsRetrySchedule(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
